import React, { useId } from "react";
import { Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useLang } from "../i18n";
import { edgeReason } from "./PdEdgeTargets";

export function PdEdgeModeControl({ pid, mode, stages, targets, capabilities, disabled, onChange, choiceRequired }) {
  const { t } = useLang();
  const chosen = Object.fromEntries(stages.flatMap(stage => {
    const info = capabilities[stage.instanceId];
    const current = targets?.[stage.key];
    const runtime = info?.targets?.some(item => item.id === current) ? current
      : info?.available && info.targets?.length === 1 ? info.targets[0].id : null;
    return runtime ? [[stage.key, runtime]] : [];
  }));
  return <Box className="pd-edge-mode" sx={{ py: 1.5, minWidth: 0 }}>
    <Typography sx={{ fontSize: 13, mb: 1, color: "var(--t2)" }}>{t("pdEdge.commonMode")}</Typography>
    <ToggleButtonGroup exclusive size="small" value={mode === "mixed" ? null : mode}
      aria-label={`${pid} · ${t("pdEdge.destination")}`} disabled={disabled || !stages.length}
      onChange={(_, value) => { const next = value || (mode !== "mixed" ? mode : null); if (next) onChange(pid, next, next === "edge" ? chosen : {}); }}>
      <ToggleButton value="cloud">{t("pdEdge.cloud")}</ToggleButton>
      <ToggleButton value="edge">{t("pdEdge.both")}</ToggleButton>
    </ToggleButtonGroup>
    {choiceRequired && mode === "cloud" && <Typography sx={{ fontSize: 13, mt: 1, color: "var(--t2)" }}>{t("pdEdge.commonChoice")}</Typography>}
    {mode === "mixed" && <Alert severity="warning" sx={{ mt: 1 }}>{t("pdEdge.mixed")}</Alert>}
  </Box>;
}

export function PdEdgeRuntime({ pid, stage, runtimeId, capabilities, disabled, onRetry, onChange }) {
  const { t, lang } = useLang();
  const id = useId();
  const info = capabilities || { loading: true };
  const targets = info.targets || [];
  const selected = targets.find(target => target.id === runtimeId);
  const ready = info.available && !info.error && !info.loading;
  const reason = info.error || info.reason || (runtimeId && !selected ? "EDGE_TARGET_INVALID" : null);
  return <Box sx={{ minWidth: 0 }}>
    {info.loading ? <CircularProgress size={18} aria-label={t("pdEdge.loading")} /> : <>
      {reason && <Typography role="alert" sx={{ fontSize: 13, color: "var(--t2)", mb: .5 }}>
        {edgeReason(reason, lang)} <Button size="small" disabled={disabled} onClick={onRetry}>{t("pdEdge.recheck")}</Button>
      </Typography>}
      {ready && targets.length > 1 ? <FormControl fullWidth size="small">
        <InputLabel id={`${id}-label`}>{t("pdEdge.runtime")}</InputLabel>
        <Select labelId={`${id}-label`} label={t("pdEdge.runtime")} value={selected?.id || ""} disabled={disabled}
          inputProps={{ "aria-label": `${pid} · ${stage.code} · ${t("pdEdge.runtime")}` }}
          onChange={event => onChange(event.target.value)} MenuProps={{ PaperProps: { className: "iio iio-review-menu" } }}>
          {targets.map(target => <MenuItem key={target.id} value={target.id}>{target.name || target.id}</MenuItem>)}
        </Select>
      </FormControl> : selected && <Typography sx={{ fontSize: 13, color: "var(--t2)", overflowWrap: "anywhere" }}>{selected.name || selected.id}</Typography>}
      {ready && !selected && <Typography sx={{ fontSize: 13, mt: .5, color: "var(--t2)" }}>{t("pdEdge.choose")}</Typography>}
    </>}
  </Box>;
}
